import { defineNuxtPlugin } from '#imports'
import { StatusCodes } from 'http-status-codes'
import { BUGSNAG_KEY } from '@shared/utils/constant'
import {
  ErrorType,
  isThrownException,
  GRPC_REQUEST_FAILED,
  TransactionException,
  GrpcUnaryRequestException
} from '@injectivelabs/exceptions'
import { IS_PRODUCTION } from '@/app/utils/constants'
import { formatErrorMessage } from '@/app/utils/errors'
import type { ThrownException } from '@injectivelabs/exceptions'

type ThrowableError = Parameters<typeof isThrownException>[0]

const reportToUser = (error: ThrownException) => {
  const { error: toastError } = useAppToast()

  // Timedout requests happening in the background should not be reported to the user
  if (
    error.type === ErrorType.HttpRequest &&
    error.code === StatusCodes.REQUEST_TOO_LONG
  ) {
    return
  }

  if (
    error instanceof GrpcUnaryRequestException &&
    error.contextCode === GRPC_REQUEST_FAILED
  ) {
    return toastError({
      title: 'The product is experiencing higher than usual demand',
      description:
        'Hang tight, engineers are doing their best to improve the performance and efficiency.'
    })
  }

  if (!(error instanceof TransactionException)) {
    return toastError({
      title: formatErrorMessage(error, error.message || 'Something happened')
    })
  }

  const title = formatErrorMessage(error, error.message)
  const description = error.originalMessage
    ? formatErrorMessage(error.originalMessage, '')
    : ''

  return toastError({
    title,
    ...(description && description !== title ? { description } : {})
  })
}

const reportUnknownErrorToBugsnag = (error: unknown) => {
  const message = formatErrorMessage(error)
  const stack = error instanceof Error ? error.stack : undefined
  const newError = new Error(
    `The ${message} is not handled as an Exception${stack ? ` - ${stack}` : ''}`
  )

  console.error(error)

  if (!BUGSNAG_KEY) {
    return
  }

  useBugsnagNotifyThrownException(
    newError,
    useSharedWalletStore().injectiveAddress
  )
}

const reportToBugSnag = (error: ThrownException) => {
  console.log(error)

  if (
    [ErrorType.Unspecified, ErrorType.WalletError].includes(error.type as any)
  ) {
    console.warn(error.toCompactError().message)
    console.error(error)

    return
  }

  if (!BUGSNAG_KEY) {
    return
  }

  useBugsnagNotifyThrownException(
    error,
    useSharedWalletStore().injectiveAddress
  )
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.config.errorHandler = (error, context) => {
    console.log(error, context)
    console.warn(error, context, (error as any)?.stack)
  }

  window.onunhandledrejection = function (event: PromiseRejectionEvent) {
    const error = event.reason

    if (!IS_PRODUCTION) {
      return console.error(error)
    }

    if (!isThrownException(error)) {
      reportUnknownErrorToBugsnag(error)
    } else {
      reportToBugSnag(error)
    }

    console.warn(error)
  }

  const errorHandler = (error: unknown) => {
    if (!isThrownException(error as ThrowableError)) {
      const { error: toastError } = useAppToast()

      toastError({
        title: formatErrorMessage(error)
      })

      return reportUnknownErrorToBugsnag(error)
    }

    const thrownError = error as ThrownException

    reportToUser(thrownError)

    if (IS_PRODUCTION) {
      reportToBugSnag(thrownError)
    }

    console.error(thrownError)
    console.warn(thrownError.toObject())
  }

  return {
    provide: {
      onError: errorHandler
    }
  }
})
